'use client';

import { useCallback, useEffect, useState } from 'react';
import Carousel from 'components/shared/Carousel/Carousel';
import CarouselContent from 'components/shared/Carousel/CarouselContent';
import { useAutoplayPlugin, useAutoplayStickyStop } from 'hooks/useAutoplayPlugin';
import useCarouselIsPlaying from 'hooks/useCarouselIsPlaying';
import type { CarouselApi } from 'types/sharedComponents';
import { cn } from 'utils/styles';
import ExplainerProgress from './ExplainerProgress';
import ExplainerSlide from './ExplainerSlide';
import { EXPLAINER_AUTOPLAY_DELAY_MS } from './consts';
import { ExplainerSlideProps } from './types';

interface ExplainerSliderProps {
  slides: ExplainerSlideProps[];
  cardIsDark: boolean;
}

const cardBaseClasses = 'relative flex h-full flex-col pt-7 md:gap-1 md:pt-13';

const ExplainerSlider = ({ slides, cardIsDark }: ExplainerSliderProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const { isPlaying } = useCarouselIsPlaying(api);
  // Stops autoplay AND keeps it stopped across embla reInits — see hook doc.
  const stopAutoplay = useAutoplayStickyStop(api);

  const autoplayPlugin = useAutoplayPlugin({
    delay: EXPLAINER_AUTOPLAY_DELAY_MS,
    stopOnInteraction: true,
    stopOnMouseEnter: false,
  });

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect).on('reInit', onSelect);
    return () => {
      api.off('select', onSelect).off('reInit', onSelect);
    };
  }, [api]);

  const goTo = useCallback(
    (index: number) => {
      stopAutoplay();
      api?.scrollTo(index);
    },
    [api, stopAutoplay],
  );

  const goPrev = useCallback(() => {
    stopAutoplay();
    api?.scrollPrev();
  }, [api, stopAutoplay]);

  const goNext = useCallback(() => {
    stopAutoplay();
    api?.scrollNext();
  }, [api, stopAutoplay]);

  const cardClasses = cn(cardBaseClasses, cardIsDark ? 'bg-reserve' : 'bg-nickel');

  return (
    <div className={cardClasses}>
      <div className="relative z-30 px-6 md:px-14">
        <ExplainerProgress
          total={slides.length}
          current={current}
          duration={EXPLAINER_AUTOPLAY_DELAY_MS / 1000}
          isPlaying={isPlaying}
          cardIsDark={cardIsDark}
          onSelect={goTo}
        />
      </div>
      <Carousel
        aria-label="Explainer slides"
        aria-roledescription="carousel"
        className="flex-1"
        setApi={setApi}
        plugins={autoplayPlugin ? [autoplayPlugin] : []}
        opts={{ align: 'start', slidesToScroll: 1, containScroll: false, loop: true }}>
        {/* Cancel CarouselContent's `-mt-4` sup-overflow offset so the slide
            fills to the panel bottom and the media sits flush. */}
        <CarouselContent className="h-full" contentClassName="h-full mt-0">
          <div className="flex h-full *:basis-full *:shrink-0 *:grow-0" aria-live="polite">
            {slides.map((slide, index) => (
              <ExplainerSlide key={slide.sys?.id ?? index} {...slide} isActive={index === current} />
            ))}
          </div>
        </CarouselContent>
      </Carousel>
      {/* Mobile-only tap zones: left half rotates back, right half rotates forward */}
      <div className="absolute inset-0 z-20 flex md:hidden" aria-hidden>
        <button type="button" className="flex-1" onClick={goPrev} aria-label="Previous slide" tabIndex={-1} />
        <button type="button" className="flex-1" onClick={goNext} aria-label="Next slide" tabIndex={-1} />
      </div>
    </div>
  );
};

export default ExplainerSlider;
